/**
 * When the client cache must be dropped — `CACHE_AUDIT.md` §5.4, implemented
 * verbatim for the events Track C step **C2.5** owns.
 *
 * A cached lookup must never outlive its user, role or company scope, so these
 * clears land with the **first** cached query, not with C4 (`PLAN_TRACK_C.md`
 * C2.5). Everything here is safe to call when nothing is cached.
 *
 * | Event | What §5.4 requires |
 * |---|---|
 * | login, logout/logout-all, expiry, role change, company switch, impersonation end | `queryClient.clear()` + the frappe-ui doc store + the stock cache + the realtime rooms |
 * | a branch switch | a narrower `removeQueries({ predicate: isBranchScoped })` — that is C4.2, deliberately not here |
 * | a 401 → refresh → retry | **nothing**; same user, same scope, clearing would thrash |
 */

import { queryClient } from './useQueryClient'
import { clearRealtime } from './realtime'
import { clearStockCache } from '../stockCache'

/** idb-keyval's database, which frappe-ui's `docStore` writes `doc:*` keys to. */
const DOC_STORE_DB = 'keyval-store'

/**
 * Delete the frappe-ui document cache.
 *
 * C0 removed the only writers (the three `useDoc` reads), so on a current build
 * there is nothing here — this sweeps what an older build left behind, which is
 * the `C-01` cross-user exposure. It is deliberately fire-and-forget: a blocked
 * or absent IndexedDB must never block a sign-out.
 */
export function clearDocStore(): void {
  try {
    if (typeof indexedDB === 'undefined') return
    indexedDB.deleteDatabase(DOC_STORE_DB)
  } catch {
    /* no IndexedDB, or a private-mode refusal: nothing to sweep */
  }
}

/**
 * Drop everything scoped to the current session: the query cache, the realtime
 * room joins, the branch-keyed stock cache and the legacy document store.
 *
 * Call this **before** the scope changes — before the new boot is applied,
 * before the company switch request, and before the redirect to `/login`.
 */
export function clearClientCache(): void {
  queryClient.clear()
  clearRealtime()
  clearStockCache()
  clearDocStore()
}
